export function macroValueIsFalsy(value) {
    return value === undefined || value === null || value === '';
}
const DESCRIPTION_MACRO_KEYS = /^(description|offer_description|short_description)$/;
/**
 * A whole `<script>` block, or a run of whitespace anywhere else. Ordered so the
 * block wins at any position where both could start, which is what keeps Pass 4's
 * collapse out of script bodies. See its comment for why that matters.
 *
 * The tag name has to end where the HTML parser ends one: at whitespace, a `/` or
 * the `>`. `\b` would not do, because `-` is not a word character, so it matched a
 * custom element named `<script-widget>` too — and `</script-widget>` does not close
 * the block, so it ran on to the next real `</script>` or to the end of the
 * template, and the markup it swallowed on the way stopped collapsing.
 */
const SCRIPT_BLOCK_OR_SPACE_RUN = /(<script(?=[\s/>])[^>]*>[\s\S]*?(?:<\/script\s*>|$))|\s{2,}/gi;
/** Quoted literal or empty `''` / `""` for COU description-only macro chains. */
function isDescriptionMacroLiteral(token) {
    const s = token.trim();
    if (s === "''" || s === '""') {
        return true;
    }
    if (s.length >= 2 && s[0] === "'" && s[s.length - 1] === "'") {
        return true;
    }
    if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') {
        return true;
    }
    return false;
}
/**
 * True when a `{{ ... }}` macro body is only `description` / `offer_description` /
 * `short_description` and `|` / quoted fallbacks (no other keys). Used so COU can
 * assign `innerHTML` with API HTML in those fields without entity-escaping.
 */
export function couMacroExpressionIsDescriptionHtmlOnly(inner) {
    const parts = inner
        .split('|')
        .map(s => s.trim())
        .filter(Boolean);
    if (parts.length === 0) {
        return false;
    }
    let sawDescriptionKey = false;
    for (const p of parts) {
        if (DESCRIPTION_MACRO_KEYS.test(p)) {
            sawDescriptionKey = true;
            continue;
        }
        if (isDescriptionMacroLiteral(p)) {
            continue;
        }
        return false;
    }
    return sawDescriptionKey;
}
/**
 * Rewrites `{{ ... }}` → `{{{ ... }}}` for description-only macro expressions so
 * `injectMacros` emits raw HTML (COU mounts via `innerHTML`).
 * Skips `{{#if`, `{{/if`, `{{else}}`, and any expression containing other keys.
 */
export function expandCouDescriptionMacrosToRawHtml(template) {
    return template.replace(/\{\{(?!\{)([\s\S]*?)\}\}/g, (full, inner) => {
        const t = inner.trim();
        if (t.startsWith('#') ||
            t.startsWith('/') ||
            t === 'else' ||
            t.startsWith('else ')) {
            return full;
        }
        if (!couMacroExpressionIsDescriptionHtmlOnly(t)) {
            return full;
        }
        return `{{{${inner}}}}`;
    });
}
/**
 * Every key a template emits as raw HTML: the keys named inside its `{{{ ... }}}`
 * expressions, each link of a fallback chain included (quoted literals are not keys).
 *
 * Run it on the *expanded* template, after `expandCouDescriptionMacrosToRawHtml`, so
 * the description fields that rewrites are covered by the same rule as an explicit
 * triple-brace. A pre-resolution pass must escape the values it substitutes into these
 * keys, because nothing escapes them later.
 *
 * Deliberately over-approximates: a `{{{key}}}` inside an `{{#if}}` arm that ends up
 * dropped still counts, and a key used both ways in one template counts as raw. One
 * pre-resolved value cannot be escaped per occurrence, so it is escaped for the
 * strictest occurrence.
 */
export function collectRawMacroKeys(template) {
    const keys = new Set();
    if (!template || typeof template !== 'string') {
        return keys;
    }
    const expressions = /\{\{\{([\s\S]*?)\}\}\}/g;
    let match;
    while ((match = expressions.exec(template)) !== null) {
        for (const part of (match[1] ?? '').split('|')) {
            const key = part.trim();
            if (key && !/^(['"])[\s\S]*\1$/.test(key)) {
                keys.add(key);
            }
        }
    }
    return keys;
}
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Resolve {{ key | fallback }} / {{{ key }}} chains against attrs.
 * Double-brace output is HTML-escaped; triple-brace (`{{{ }}}`) is raw (opt-in).
 */
export function resolveMacroReplacement(content, attrs, raw) {
    content = content.trim();
    if (content.indexOf('#if') === 0 ||
        content === 'else' ||
        content === '/if') {
        return '';
    }
    const fallbackChain = content.split('|').map((item) => item.trim());
    const lastItem = fallbackChain[fallbackChain.length - 1] ?? '';
    const quotedMatch = lastItem.match(/^(['"])(.*)\1$/);
    const isLastItemQuoted = !!quotedMatch;
    const itemsToCheck = isLastItemQuoted
        ? fallbackChain.length - 1
        : fallbackChain.length;
    for (let i = 0; i < itemsToCheck; i++) {
        const key = fallbackChain[i];
        if (key === undefined) {
            continue;
        }
        const value = attrs[key];
        if (Object.prototype.hasOwnProperty.call(attrs, key) &&
            !macroValueIsFalsy(value)) {
            const s = String(value);
            return raw ? s : escapeHtml(s);
        }
    }
    const defaultStr = isLastItemQuoted && quotedMatch ? String(quotedMatch[2] ?? '') : '';
    return raw ? defaultStr : escapeHtml(defaultStr);
}
function injectMacrosImpl(text, attributes, escapeValues) {
    if (!text || typeof text !== 'string') {
        return text || '';
    }
    let working = text;
    const attrs = attributes && typeof attributes === 'object' ? attributes : {};
    // --- Pass 1: Handlebars-style conditionals {{#if key}}...{{else}}...{{/if}} ---
    (function processConditionals() {
        const idx = working.indexOf('{{#if');
        if (idx === -1) {
            return;
        }
        const closeTag = working.indexOf('}}', idx);
        if (closeTag === -1) {
            return;
        }
        const keyStr = working.substring(idx + 5, closeTag).trim();
        const key = keyStr.split(/\s/)[0];
        const blockStart = closeTag + 2;
        let depth = 1;
        let i = blockStart;
        let elseIndex = -1;
        let endIndex = -1;
        const openLen = 6; // '{{#if'
        const closeLen = 7; // '{{/if}}'
        const elseLen = 8; // '{{else}}'
        while (i < working.length) {
            if (working.substring(i, i + openLen) === '{{#if' &&
                /[\s\t]/.test(working.charAt(i + openLen))) {
                depth += 1;
                i += openLen;
                continue;
            }
            if (working.substring(i, i + closeLen) === '{{/if}}') {
                depth -= 1;
                if (depth === 0) {
                    endIndex = i;
                    break;
                }
                i += closeLen;
                continue;
            }
            if (depth === 1 && working.substring(i, i + elseLen) === '{{else}}') {
                elseIndex = elseIndex === -1 ? i : elseIndex;
            }
            i += 1;
        }
        if (endIndex === -1) {
            return;
        }
        const ifContent = elseIndex === -1
            ? working.substring(blockStart, endIndex)
            : working.substring(blockStart, elseIndex);
        const elseContent = elseIndex === -1 ? '' : working.substring(elseIndex + elseLen, endIndex);
        const value = attrs[key];
        const chosen = macroValueIsFalsy(value) ? elseContent : ifContent;
        working =
            working.substring(0, idx) +
                chosen +
                working.substring(endIndex + closeLen);
        processConditionals();
    })();
    // --- Pass 2: Triple-brace raw {{{ key | fallback }}} ---
    (function processRawMacros() {
        for (;;) {
            const start = working.indexOf('{{{');
            if (start === -1) {
                return;
            }
            const end = working.indexOf('}}}', start + 3);
            if (end === -1) {
                return;
            }
            const inner = working.substring(start + 3, end);
            const resolved = resolveMacroReplacement(inner, attrs, true);
            working =
                working.substring(0, start) + resolved + working.substring(end + 3);
        }
    })();
    // --- Pass 3: Double-brace escaped {{ key | fallback }} ---
    working = working.replace(/\{\{(.*?)\}\}/g, (_match, content) => {
        return resolveMacroReplacement(String(content), attrs, !escapeValues);
    });
    // --- Pass 4: Collapse multiple spaces to single space (no double spaces) ---
    /**
     * Cosmetic only, and it must not reach inside a `<script>`: collapsing the
     * newlines in a script body turns a `//` comment into a swallow of the rest
     * of it, so the closing braces vanish and the script dies on a syntax error.
     *
     * A script block is therefore an alternative of the same scan, tried at every
     * position before the whitespace run, and returned verbatim when it matches.
     * The markup between blocks meets the identical `\s{2,}` it always did, so it
     * collapses byte for byte as before.
     *
     * The block runs to the first `</script>`, or to the end of the template when
     * there is none, which is exactly what the HTML parser treats as script text
     * in either case: nothing skipped here is markup, nothing collapsed is code.
     */
    working = working
        .replace(SCRIPT_BLOCK_OR_SPACE_RUN, (_match, scriptBlock) => scriptBlock === undefined ? ' ' : scriptBlock)
        .trim();
    return working;
}
/**
 * Resolve macros for a sink that assigns the result as HTML (`innerHTML` /
 * `dangerouslySetInnerHTML`). Double-brace values are HTML-escaped; triple-brace
 * stays raw.
 */
export function injectMacros(text, attributes) {
    return injectMacrosImpl(text, attributes, true);
}
/**
 * Resolve macros for a sink that renders the result as a text node (a JSX text
 * child). Values are substituted verbatim: Preact writes them as text, so escaping
 * here would put the entity itself on screen (`o&#39;blah` instead of `o'blah`).
 * Never hand this output to an HTML sink.
 */
export function injectMacrosText(text, attributes) {
    return injectMacrosImpl(text, attributes, false);
}
